import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { friendlyError } from '../lib/errors.js'
import { Dialog } from './Dialog.js'
import { Button } from './Button.js'
import { Field, Input, Select } from './Field.js'
import { Money } from './Money.js'

interface PayableInvoice {
  id: string
  invoiceNo: string
  studentName: string
  finalAmountInr: number
  amountPaidInr: number
  balanceInr: number
}

interface RecordPaymentDialogProps {
  /** The invoice to record a payment for, or null when closed. */
  invoice: PayableInvoice | null
  onClose: () => void
  onRecorded: () => void
}

const rupeesToPaise = (s: string) => Math.round((parseFloat(s) || 0) * 100)

/**
 * Record a cash/UPI payment against an invoice. Supports partial payments: the
 * amount defaults to the full remaining balance (the common case) but the admin
 * can edit it down. The server derives the invoice status from the running total
 * and rejects overpayment; we also cap client-side for a friendlier message.
 */
export function RecordPaymentDialog({ invoice, onClose, onRecorded }: RecordPaymentDialogProps) {
  const balancePaise = invoice?.balanceInr ?? 0
  const [amountRupees, setAmountRupees] = useState('')
  const [method, setMethod] = useState<'cash' | 'upi'>('cash')
  const [error, setError] = useState<string | null>(null)

  // Reset the form each time a new invoice opens; default to the full balance.
  useEffect(() => {
    if (invoice) {
      setAmountRupees((invoice.balanceInr / 100).toString())
      setMethod('cash')
      setError(null)
    }
  }, [invoice])

  const amountPaise = rupeesToPaise(amountRupees)
  const overBalance = amountPaise > balancePaise
  const invalid = amountPaise <= 0 || overBalance

  const record = useMutation({
    mutationFn: async () => {
      if (!invoice) return
      const res = await api(`/api/v1/fee-invoices/${invoice.id}/mark-cash-paid`, {
        method: 'POST',
        body: JSON.stringify({ amountInr: amountPaise, method }),
      })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: () => {
      onRecorded()
      onClose()
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not record payment'),
  })

  return (
    <Dialog
      open={!!invoice}
      onClose={onClose}
      title="Record payment"
      description={
        invoice
          ? `${invoice.studentName} · ${invoice.invoiceNo}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => record.mutate()}
            disabled={invalid || record.isPending}
          >
            {record.isPending ? 'Recording…' : 'Record payment'}
          </Button>
        </>
      }
    >
      {invoice ? (
        <div className="space-y-4">
          <div className="rounded-md bg-surface-subtle px-3 py-2 text-sm text-fg-muted">
            <div className="flex justify-between">
              <span>Invoice total</span>
              <Money paise={invoice.finalAmountInr} variant="data" />
            </div>
            <div className="flex justify-between">
              <span>Already paid</span>
              <Money paise={invoice.amountPaidInr} variant="data" />
            </div>
            <div className="flex justify-between font-medium text-fg">
              <span>Balance due</span>
              <Money paise={invoice.balanceInr} variant="data" />
            </div>
          </div>

          <Field label="Amount received (₹)" required>
            <Input
              numeric
              value={amountRupees}
              onChange={(e) => setAmountRupees(e.target.value)}
              placeholder="₹"
              autoFocus
            />
          </Field>

          <Field label="Method">
            <Select value={method} onChange={(e) => setMethod(e.target.value as 'cash' | 'upi')}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
            </Select>
          </Field>

          {overBalance ? (
            <p className="text-xs text-danger">
              Amount cannot exceed the balance due.
            </p>
          ) : null}
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      ) : null}
    </Dialog>
  )
}
